package com.example.readingnotes.presentation.postlist

import com.example.readingnotes.domain.usecase.ClearSearchHistoryUseCase
import com.example.readingnotes.domain.usecase.GetSearchHistoryUseCase
import com.example.readingnotes.domain.usecase.GetSearchSuggestionsUseCase
import com.example.readingnotes.domain.usecase.LoadPopularTagsUseCase
import com.example.readingnotes.domain.usecase.LoadPostListUseCase
import com.example.readingnotes.domain.usecase.LoadPostListWithPaginationUseCase
import com.example.readingnotes.domain.usecase.SearchPostsUseCase
import com.example.readingnotes.platform.FavoriteSearch
import com.example.readingnotes.platform.KeyValueStorage
import com.example.readingnotes.platform.createKeyValueStorage
import com.example.readingnotes.util.ErrorMessageProvider
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock

class PostListViewModel(
    private val loadPostListUseCase: LoadPostListUseCase,
    private val loadPostListWithPaginationUseCase: LoadPostListWithPaginationUseCase,
    private val searchPostsUseCase: SearchPostsUseCase,
    private val loadPopularTagsUseCase: LoadPopularTagsUseCase,
    private val getSearchSuggestionsUseCase: GetSearchSuggestionsUseCase,
    private val getSearchHistoryUseCase: GetSearchHistoryUseCase,
    private val clearSearchHistoryUseCase: ClearSearchHistoryUseCase,
    private val viewModelScope: CoroutineScope,
) {
    private val _state = MutableStateFlow(PostListState())
    val state: StateFlow<PostListState> = _state.asStateFlow()

    private val storage: KeyValueStorage = createKeyValueStorage()

    companion object {
        private const val KEY_PAGE_SIZE = "search_page_size"
        private const val KEY_AUTO_SEARCH = "search_auto_search"
        private const val KEY_FAVORITES = "search_favorites"
    }

    fun onEvent(event: PostListEvent) {
        when (event) {
            is PostListEvent.OnAppear -> {
                loadSettings()
                loadFavoriteSearches()
                loadPosts(isRefresh = false)
                loadPopularTags()
                loadSearchHistory()
            }
            is PostListEvent.OnRefresh -> loadPosts(isRefresh = true)
            is PostListEvent.OnLoadMore -> loadMorePosts()
            is PostListEvent.OnRetry -> loadPosts(isRefresh = false)
            is PostListEvent.OnSearchQueryChanged -> {
                _state.update {
                    it.copy(
                        searchQuery = event.query,
                        isSearchActive = event.query.isNotBlank(),
                        showSuggestions = event.query.isNotBlank(),
                    )
                }
                // Load suggestions for autocomplete
                if (event.query.isNotBlank() && event.query.length >= 2) {
                    loadSuggestions(event.query)
                } else {
                    _state.update { it.copy(searchSuggestions = emptyList()) }
                }
                // Auto-search after debounce (if enabled)
                if (event.query.isNotBlank() && _state.value.autoSearch) {
                    searchPosts()
                } else {
                    _state.update {
                        it.copy(
                            isSearchActive = false,
                            searchResults = emptyList(),
                        )
                    }
                }
            }
            is PostListEvent.OnSearchSubmit -> {
                _state.update { it.copy(showSuggestions = false) }
                searchPosts()
            }
            is PostListEvent.OnClearSearch -> {
                _state.update {
                    it.copy(
                        searchQuery = "",
                        isSearchActive = false,
                        searchResults = emptyList(),
                        searchSuggestions = emptyList(),
                        showSuggestions = false,
                    )
                }
            }
            is PostListEvent.OnShowSuggestions -> {
                _state.update { it.copy(showSuggestions = true) }
            }
            is PostListEvent.OnHideSuggestions -> {
                _state.update { it.copy(showSuggestions = false) }
            }
            is PostListEvent.OnSuggestionsLoaded -> {
                _state.update { it.copy(searchSuggestions = event.suggestions) }
            }
            is PostListEvent.OnSortChanged -> {
                _state.update { it.copy(sortBy = event.sortBy) }
                saveSettings()
                if (_state.value.isSearchActive) {
                    searchPosts()
                } else {
                    loadPosts(isRefresh = true)
                }
            }
            is PostListEvent.OnStatusFilterChanged -> {
                _state.update { it.copy(statusFilter = event.status) }
                if (_state.value.isSearchActive) {
                    searchPosts()
                } else {
                    loadPosts(isRefresh = true)
                }
            }
            is PostListEvent.OnTagFilterToggled -> {
                val currentTags = _state.value.selectedTags
                val newTags =
                    if (currentTags.contains(event.tag)) {
                        currentTags - event.tag
                    } else {
                        currentTags + event.tag
                    }
                _state.update { it.copy(selectedTags = newTags) }

                if (_state.value.isSearchActive || newTags.isNotEmpty()) {
                    searchPosts()
                }
            }
            is PostListEvent.OnClearTagFilters -> {
                _state.update { it.copy(selectedTags = emptySet()) }
                if (_state.value.isSearchActive) {
                    searchPosts()
                } else {
                    loadPosts(isRefresh = true)
                }
            }
            is PostListEvent.OnPopularTagsLoaded -> {
                _state.update { it.copy(popularTags = event.tags) }
            }
            is PostListEvent.OnSearchHistoryLoaded -> {
                _state.update { it.copy(searchHistory = event.history) }
            }
            is PostListEvent.OnClearSearchHistory -> {
                clearHistory()
            }
            is PostListEvent.OnPageSizeChanged -> {
                _state.update { it.copy(pageSize = event.pageSize) }
                saveSettings()
            }
            is PostListEvent.OnAutoSearchToggled -> {
                _state.update { it.copy(autoSearch = event.enabled) }
                saveSettings()
            }
            is PostListEvent.OnShowFavoriteDialog -> {
                _state.update { it.copy(showFavoriteDialog = true) }
            }
            is PostListEvent.OnHideFavoriteDialog -> {
                _state.update { it.copy(showFavoriteDialog = false, favoriteNameInput = "") }
            }
            is PostListEvent.OnFavoriteNameInputChanged -> {
                _state.update { it.copy(favoriteNameInput = event.name) }
            }
            is PostListEvent.OnSaveFavoriteSearch -> {
                saveFavoriteSearch()
            }
            is PostListEvent.OnLoadFavoriteSearch -> {
                loadFavoriteSearch(event.favorite)
            }
            is PostListEvent.OnDeleteFavoriteSearch -> {
                deleteFavoriteSearch(event.favoriteName)
            }
            is PostListEvent.OnFavoriteSearchesLoaded -> {
                _state.update { it.copy(favoriteSearches = event.favorites) }
            }
        }
    }

    private fun loadSettings() {
        val pageSize = storage.getInt(KEY_PAGE_SIZE, 20)
        val autoSearch = storage.getBoolean(KEY_AUTO_SEARCH, true)
        _state.update { it.copy(pageSize = pageSize, autoSearch = autoSearch) }
    }

    private fun saveSettings() {
        storage.setInt(KEY_PAGE_SIZE, _state.value.pageSize)
        storage.setBoolean(KEY_AUTO_SEARCH, _state.value.autoSearch)
    }

    private fun saveFavoriteSearch() {
        val currentState = _state.value
        if (currentState.favoriteNameInput.isBlank()) return

        val favorite =
            FavoriteSearch(
                name = currentState.favoriteNameInput,
                query = currentState.searchQuery,
                tags = currentState.selectedTags,
                status = currentState.statusFilter,
                createdAt = Clock.System.now().toString(),
            )

        val updatedFavorites = currentState.favoriteSearches + favorite
        _state.update { it.copy(favoriteSearches = updatedFavorites) }

        // Save to storage as JSON
        val favoritesJson =
            kotlinx.serialization.json.Json.encodeToString(
                kotlinx.serialization.serializer<List<FavoriteSearch>>(),
                updatedFavorites,
            )
        storage.setString(KEY_FAVORITES, favoritesJson)

        _state.update { it.copy(showFavoriteDialog = false, favoriteNameInput = "") }
    }

    private fun loadFavoriteSearch(favorite: FavoriteSearch) {
        _state.update {
            it.copy(
                searchQuery = favorite.query,
                selectedTags = favorite.tags,
                statusFilter = favorite.status,
                isSearchActive = true,
                showSuggestions = false,
            )
        }
        searchPosts()
    }

    private fun deleteFavoriteSearch(name: String) {
        val updatedFavorites = _state.value.favoriteSearches.filterNot { it.name == name }
        _state.update { it.copy(favoriteSearches = updatedFavorites) }

        val favoritesJson =
            kotlinx.serialization.json.Json.encodeToString(
                kotlinx.serialization.serializer<List<FavoriteSearch>>(),
                updatedFavorites,
            )
        storage.setString(KEY_FAVORITES, favoritesJson)
    }

    private fun loadFavoriteSearches() {
        val favoritesJson = storage.getString(KEY_FAVORITES)
        if (favoritesJson != null) {
            try {
                val favorites =
                    kotlinx.serialization.json.Json.decodeFromString<List<FavoriteSearch>>(
                        favoritesJson,
                    )
                _state.update { it.copy(favoriteSearches = favorites) }
            } catch (e: Exception) {
                // Ignore parse errors
            }
        }
    }

    private fun loadPosts(isRefresh: Boolean) {
        viewModelScope.launch {
            val page = if (isRefresh) 1 else _state.value.currentPage
            _state.update {
                if (isRefresh) {
                    it.copy(isRefreshing = true, currentPage = 1)
                } else {
                    it.copy(isLoading = true, errorMessage = null)
                }
            }

            loadPostListWithPaginationUseCase(page)
                .onSuccess { paginatedPosts ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isRefreshing = false,
                            posts = if (isRefresh) paginatedPosts.posts else paginatedPosts.posts,
                            currentPage = paginatedPosts.currentPage,
                            totalPages = paginatedPosts.totalPages,
                            hasMore = paginatedPosts.hasMore,
                            errorMessage = null,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            isRefreshing = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "読み込みに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun loadMorePosts() {
        val currentState = _state.value
        if (currentState.isLoadingMore || !currentState.hasMore) return

        viewModelScope.launch {
            _state.update { it.copy(isLoadingMore = true) }

            val nextPage = currentState.currentPage + 1
            loadPostListWithPaginationUseCase(nextPage)
                .onSuccess { paginatedPosts ->
                    _state.update {
                        it.copy(
                            isLoadingMore = false,
                            posts = currentState.posts + paginatedPosts.posts,
                            currentPage = paginatedPosts.currentPage,
                            totalPages = paginatedPosts.totalPages,
                            hasMore = paginatedPosts.hasMore,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoadingMore = false,
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "追加読み込みに失敗しました"),
                        )
                    }
                }
        }
    }

    private fun searchPosts() {
        val currentState = _state.value
        if (currentState.searchQuery.isBlank()) return

        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, errorMessage = null) }

            val sortByString =
                when (currentState.sortBy) {
                    SortOption.OLDEST -> "oldest"
                    SortOption.TITLE_ASC -> "title_asc"
                    SortOption.TITLE_DESC -> "title_desc"
                    else -> "newest"
                }

            searchPostsUseCase(
                query = currentState.searchQuery,
                sortBy = sortByString,
                status = currentState.statusFilter,
                tags = currentState.selectedTags,
            ).onSuccess { results ->
                _state.update {
                    it.copy(
                        isLoading = false,
                        searchResults = results,
                        errorMessage = null,
                    )
                }
            }.onFailure { error ->
                _state.update {
                    it.copy(
                        isLoading = false,
                        errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "検索に失敗しました"),
                    )
                }
            }
        }
    }

    private fun loadSuggestions(query: String) {
        viewModelScope.launch {
            _state.update { it.copy(isLoadingSuggestions = true) }

            getSearchSuggestionsUseCase(query)
                .onSuccess { suggestions ->
                    _state.update {
                        it.copy(
                            isLoadingSuggestions = false,
                            searchSuggestions = suggestions,
                        )
                    }
                }.onFailure {
                    _state.update { it.copy(isLoadingSuggestions = false) }
                }
        }
    }

    private fun loadPopularTags() {
        viewModelScope.launch {
            _state.update { it.copy(isLoadingTags = true) }

            loadPopularTagsUseCase()
                .onSuccess { tags ->
                    _state.update {
                        it.copy(
                            isLoadingTags = false,
                            popularTags = tags,
                        )
                    }
                }.onFailure { error ->
                    _state.update {
                        it.copy(
                            isLoadingTags = false,
                        )
                    }
                }
        }
    }

    private fun loadSearchHistory() {
        viewModelScope.launch {
            getSearchHistoryUseCase()
                .onSuccess { history ->
                    _state.update { it.copy(searchHistory = history.map { it.query }) }
                }.onFailure {
                    // Ignore errors, just keep empty history
                    _state.update { it.copy(searchHistory = emptyList()) }
                }
        }
    }

    private fun clearHistory() {
        viewModelScope.launch {
            clearSearchHistoryUseCase()
                .onSuccess {
                    _state.update { it.copy(searchHistory = emptyList()) }
                }.onFailure { error ->
                    // Show error but still clear local state
                    _state.update {
                        it.copy(
                            searchHistory = emptyList(),
                            errorMessage = ErrorMessageProvider.getLocalizedMessage(error, "履歴のクリアに失敗しました"),
                        )
                    }
                }
        }
    }
}
